import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../../api/client";
import type {
  CompositionSide,
  CompositionUnit,
  CompositionWrite,
  Deployment,
  EniRole,
  Phase,
  UnitRole,
} from "../../api/types";
import { isCompany } from "../../company";
import { designateSoon } from "../../components/attention";
import { unitDesignation } from "../../i18n/designation";
import { t } from "../../i18n/i18n";
import { useDossierLocked } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { paragraphLabel, paragraphOf, roleShort } from "../../views/composition/roles";
import { type SideView, sideView, unitById, unitByKey } from "../../views/composition/rows";
import { dossierWrite, recordWrite, undoAction, useDossierHistory, writeFailure } from "../dossierWrites";
import { useHistoryState } from "../history";
import { defensiveRoles } from "./propagation";
import { orderActions, withOrders } from "../orderSync";

/** Écritures de la composition du dossier (docs/18 §4, §6) : ajouter, retirer, rétablir une unité, tout rétablir d'un
 *  camp (`PUT /composition`), choisir un rôle (`PUT /roles` ; `PUT /eni-roles` sur un serveur qui ne publie pas encore
 *  la composition), poser une unité à placer sur la carte (`PUT /positions`) ou l'en retirer (`DELETE /positions`).
 *  Toutes passent par la portée du dossier, s'inscrivent dans le canal « dispositif » de l'historique (⌘Z), et leur
 *  avis porte « Annuler », puis l'action vers l'ordre (`orderActions` : « Voir les changements », « Voir l'ordre »…). Le catalogue et les ordres de bataille restent inchangés : tout est propre au dossier. */

/** Sélecteur d'une ligne d'unité du dossier dans les listes (désignée après une écriture). */
export function unitRowSelector(id: string): string {
  return `[data-composition-unit="${CSS.escape(id)}"]`;
}

/** Désigne la ligne d'une unité quand elle apparaît (listes montées en douceur). */
export function designateUnitRow(id: string | null | undefined): void {
  if (!id || typeof document === "undefined" || typeof CSS === "undefined") return;
  designateSoon(unitRowSelector(id), "ring");
}

/** Ouverture de l'ordre depuis un avis : déplacées dans `map/orderSync.ts` (toutes les écritures du dossier). */
export { openFinalizedOrder, openOutdatedOrder } from "../orderSync";

/** Emplacement et missions d'une unité dans un dispositif, en texte comparable (vide si elle n'y est pas). */
function placementOf(deployment: Deployment | undefined, key: string): string {
  const unit = deployment?.units.find((u) => u.key === key);
  if (!unit) return "";
  return JSON.stringify(
    Object.entries(unit.positions)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([phase, p]) => [phase, p?.lat.toFixed(5), p?.lon.toFixed(5), p?.mission]),
  );
}

/** Unités placées avant l'écriture et sorties de la carte par elle (autres que `except`) : un rôle choisi a pris leur
 *  emplacement. */
export function displacedUnits(
  before: SideView | null,
  after: SideView | null,
  except: string,
): CompositionUnit[] {
  if (!before || !after) return [];
  const placed = new Set(before.out.units.filter((u) => u.placed && u.depth > 0).map((u) => u.id));
  return after.out.units.filter((u) => u.id !== except && placed.has(u.id) && !u.placed);
}

/** Avis d'une unité ajoutée sans emplacement de la manœuvre : rattachée à la force, ou à une formation subordonnée
 *  (unité amie : l'OPO la cite dans les renforcements, « au profit de » cette formation, sans en faire une unité de
 *  manœuvre du chef). */
export function attachedNote(view: SideView | null, unit: CompositionUnit | undefined, result: Deployment): string {
  const parent = unit?.parent ? view?.out.units.find((u) => u.code === unit.parent || u.id === unit.parent) : undefined;
  if (!parent || parent.depth < 1) return t("Elle est rattachée à la force, sans emplacement de la manœuvre.");
  const cited =
    unit?.side === "AMI" && Object.values(result.orders?.changed ?? {}).some((keys) => keys.includes("reinforcements"));
  return cited
    ? t("Elle est rattachée à {parent} : l'OPO la cite dans les renforcements.", { parent: parent.label })
    : t("Elle est rattachée à {parent}, sans emplacement de la manœuvre.", { parent: parent.label });
}

const SIDE_LABEL = {
  AMI: () => t("des unités amies"),
  ENI: () => t("des unités ennemies"),
};

export function useCompositionWrites() {
  const locked = useDossierLocked();
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const notify = useUi((s) => s.notify);
  const historyBusy = useHistoryState((s) => s.busy);
  useDossierHistory();

  const compose = useMutation({
    mutationFn: (body: CompositionWrite) =>
      dossierWrite(queryClient, (target, opo) => api.setComposition(target, body, opo), {
        notSaved: t("Enregistrez le dossier pour composer ses forces."),
        channel: "dispositif",
      }),
    onSuccess: (write, body) => {
      const before = sideView(write.previous, body.side);
      const after = sideView(write.result, body.side);
      // Unité concernée : celle de la requête (retrait, rétablissement), ou la clé « ADD-… » rendue par le serveur (ajout).
      const last =
        body.op === "remove" || body.op === "restore" || body.op === "command"
          ? body.key
          : body.op === "add"
            ? (write.result.composition?.last?.id ?? null)
            : null;
      const unit = last ? (unitById(after, last) ?? unitById(before, last)) : undefined;
      const removed = body.op === "remove" ? before?.out.units.find((u) => u.id === body.key) : undefined;
      const label = unitDesignation(unit?.label ?? removed?.label ?? (last ? (after?.out.removed.find((r) => r.id === last)?.label ?? last) : ""));
      let text: string;
      let detail: string;
      if (body.op === "add") {
        text = t("{unit} ajoutée au dossier", { unit: label });
        detail = unit?.placed
          ? t("Elle prend un emplacement de la manœuvre.")
          : unit?.placeable
            ? t("Elle est à placer : glissez-la sur la carte depuis la liste.")
            : attachedNote(after, unit, write.result);
      } else if (body.op === "remove") {
        text = t("{unit} retirée du dossier", { unit: label });
        detail = t("Le catalogue et l'ordre de bataille restent inchangés ; « Rétablir » la remet dans ce dossier.");
      } else if (body.op === "restore") {
        text = t("{unit} rétablie dans le dossier", { unit: label });
        detail = unit?.placed
          ? t("Elle reprend un emplacement de la manœuvre.")
          : t("Elle est à placer : glissez-la sur la carte depuis la liste.");
      } else if (body.op === "command") {
        // Unité reçue par un S/GTIA (docs/21, décision 5) : l'en-tête de l'OPO la cite avec sa relation.
        text = t("Relation de commandement de {unit} : {command}", { unit: label, command: body.command ?? "TACON" });
        detail = t("L'en-tête de l'OPO cite l'unité reçue avec cette relation de commandement.");
      } else {
        text = t("Composition {side} rétablie", { side: SIDE_LABEL[body.side]() });
        detail = t("Unités ajoutées retirées, unités retirées rétablies, rôles par défaut.");
      }
      const actionLabel =
        body.op === "add"
          ? t("ajout de {unit}", { unit: label })
          : body.op === "remove"
            ? t("retrait de {unit} du dossier", { unit: label })
            : body.op === "restore"
              ? t("rétablissement de {unit}", { unit: label })
              : body.op === "command"
                ? t("relation de commandement de {unit}", { unit: label })
                : t("composition rétablie");
      const undo = undoAction(recordWrite(write, actionLabel));
      notify({
        text,
        detail: withOrders(detail, write.result),
        tone: body.op === "remove" ? "info" : "ok",
        ...orderActions(undo, write.result),
      });
      if ((body.op === "add" || body.op === "restore") && unit && !unit.placed) designateUnitRow(unit.id);
    },
    onError: (error) => writeFailure(queryClient, error, t("Composition non enregistrée")),
  });

  const setRole = useMutation({
    mutationFn: (input: { side: CompositionSide; unit: string; role: UnitRole | null }) =>
      dossierWrite(
        queryClient,
        (target, opo, current) =>
          current.composition
            ? api.setRole(target, input, opo)
            : // Serveur qui ne publie pas encore la composition : rôle ennemi par la route d'avant (alias).
              api.setEniRole(target, input.unit, input.role as EniRole | null),
        { notSaved: t("Enregistrez le dossier pour changer le rôle des unités."), channel: "dispositif" },
      ),
    onSuccess: (write, input) => {
      const view = sideView(write.result, input.side);
      const unit = unitById(view, input.unit);
      const label = unitDesignation(unit?.label ?? input.unit);
      const role = unit?.role ?? input.role;
      // Niveau compagnie (docs/21 §4) : rôles des emplacements du S/GTIA (« Appui », « Pion de tête »).
      const company = isCompany(write.result.level) && input.side === "AMI";
      // Rôles d'une défense (registre A126) : « 1er échelon défensif »…
      const defensive = input.side === "AMI" && defensiveRoles(write.result);
      const undo = undoAction(recordWrite(write, t("rôle de {unit}", { unit: label })));
      const paragraph = unit ? paragraphLabel(unit.opo ?? paragraphOf(unit.side, unit.role), company, defensive) : "";
      const parts = [paragraph ? t("Paragraphe de l'OPO : {paragraph}.", { paragraph }) : null];
      // Rôle refusé par l'emplacement qu'elle tenait : l'unité est à placer, sa ligne est désignée.
      const before = sideView(write.previous, input.side);
      const wasPlaced = unitById(before, input.unit)?.placed ?? false;
      const nowToPlace = Boolean(unit && !unit.placed && unit.placeable && wasPlaced);
      // Autre unité sortie de la carte (son emplacement va au rôle choisi) : nommée, sa ligne désignée.
      const displaced = displacedUnits(before, view, unit?.id ?? input.unit);
      if (nowToPlace) parts.push(t("L'unité est à placer : glissez-la sur la carte depuis la liste."));
      for (const other of displaced)
        parts.push(t("{unit} quitte son emplacement : à placer depuis la liste.", { unit: unitDesignation(other.label) }));
      // Dit d'après le dispositif rendu : l'emplacement et les missions ont-ils changé ?
      const moved = unit ? placementOf(write.previous, unit.key) !== placementOf(write.result, unit.key) : true;
      if (!nowToPlace && displaced.length === 0)
        parts.push(
          moved
            ? t("Les emplacements de la manœuvre, les missions et l'ordre suivent.")
            : t("Son emplacement et ses missions ne changent pas : l'ordre la cite dans ce rôle."),
        );
      notify({
        text: role
          ? t("Rôle de {unit} : {role}", { unit: label, role: roleShort(role, company, defensive) })
          : t("Rôle de {unit} : automatique", { unit: label }),
        detail: withOrders(parts.filter(Boolean).join(" "), write.result),
        tone: "ok",
        ...orderActions(undo, write.result),
      });
      if (nowToPlace && unit) designateUnitRow(unit.id);
      else if (displaced[0]) designateUnitRow(displaced[0].id);
    },
    onError: (error) => writeFailure(queryClient, error, t("Rôle non enregistré")),
  });

  const place = useMutation({
    mutationFn: (input: { unit: string; phase: Phase; lon: number; lat: number }) =>
      dossierWrite(queryClient, (target, opo) => api.moveUnit(target, { ...input, opo }), {
        notSaved: t("Enregistrez le dossier pour placer les unités."),
        channel: "dispositif",
      }),
    onSuccess: (write, input) => {
      const unit = write.result.units.find((u) => u.key === input.unit);
      const name = unitDesignation(unit?.display_name || unit?.designation || input.unit);
      const locality = unit?.positions[input.phase]?.locality;
      const undo = undoAction(recordWrite(write, t("pose de {unit}", { unit: name })));
      notify({
        text: t("{unit} placée en {phase}", { unit: name, phase: input.phase }) + (locality ? ` — ${locality}` : ""),
        detail: withOrders(
          t("Elle reste sur la carte aux temps suivants ; sa mission par défaut suit son rôle et se change par le menu de mission."),
          write.result,
        ),
        tone: "ok",
        ...orderActions(undo, write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Unité non placée")),
  });

  const unplace = useMutation({
    mutationFn: (unit: string) =>
      dossierWrite(queryClient, (target, opo) => api.resetPositions(target, { unit }, opo), {
        notSaved: t("Dossier non enregistré"),
        channel: "dispositif",
      }),
    onSuccess: (write, key) => {
      const unit = unitByKey(sideView(write.previous, "AMI"), key) ?? unitByKey(sideView(write.previous, "ENI"), key);
      const name = unitDesignation(unit?.label ?? key);
      const undo = undoAction(recordWrite(write, t("retrait de {unit}", { unit: name })));
      notify({
        text: t("{unit} retirée de la carte", { unit: name }),
        detail: withOrders(t("Elle revient dans la liste des unités à placer."), write.result),
        tone: "info",
        ...orderActions(undo, write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Retrait non enregistré")),
  });

  return {
    canEdit: Boolean(params?.exercise_id) && !locked,
    compose,
    setRole,
    place,
    unplace,
    busy: historyBusy || compose.isPending || setRole.isPending || place.isPending || unplace.isPending,
  };
}
